import { getSearchParams } from "../../../utils/request";
import { api } from "../../api";

const customersApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getCustomers: builder.query({
      query: ({ page = 1, limit = 20, ...rest }) => {
        const query = getSearchParams({
          legacy: false,
          page,
          limit,
          ...rest,
        });
        return `customers?${query}`;
      },
      transformResponse: (response, _meta, arg = {}) => {
        const rawCustomers =
          (Array.isArray(response?.data) && response.data) ||
          response?.customer ||
          response?.customers ||
          [];
        const page = Number(response?.page || response?.currentPage || arg.page || 1);
        const limit = Number(arg.limit || response?.limit || rawCustomers.length || 20);
        const hasServerPagination =
          response?.total != null ||
          response?.totalDocs != null ||
          response?.totalPages != null;
        const total = Number(
          response?.total ?? response?.totalDocs ?? rawCustomers.length
        );
        const totalPages = Math.max(
          1,
          Number(response?.totalPages || Math.ceil(total / limit) || 1)
        );
        const customers = hasServerPagination
          ? rawCustomers
          : rawCustomers.slice((page - 1) * limit, page * limit);

        return {
          ...response,
          data: customers,
          customers,
          total,
          totalPages,
          page,
          nextPage: response?.nextPage ?? (page < totalPages ? page + 1 : null),
          hasNext: response?.hasNext ?? page < totalPages,
          hasPrev: response?.hasPrev ?? page > 1,
        };
      },
      providesTags: (data) => {
        const customers =
          (Array.isArray(data?.data) && data.data) ||
          data?.customer ||
          data?.customers ||
          [];

        return customers.length > 0
          ? [
              ...customers.map(({ _id }) => ({
                type: "Customers",
                id: _id,
              })),
              { type: "Customers", id: "LIST" },
            ]
          : [{ type: "Customers", id: "LIST" }];
      },
    }),
    addCustomer: builder.mutation({
      query(body) {
        return {
          url: "onboarding/customer",
          method: "POST",
          body,
        };
      },
      invalidatesTags: [{ type: "Customers", id: "LIST" }],
    }),
    resendCustomerInvite: builder.mutation({
      query({ id }) {
        return {
          url: `onboarding/customer/${id}/resend-invite`,
          method: "POST",
        };
      },
      invalidatesTags: (_result, _error, { id }) => {
        return [{ type: "Customers", id }];
      },
    }),
    requestCustomerInvite: builder.mutation({
      query({ id }) {
        return {
          url: `onboarding/customer/${id}/request-invite`,
          method: "POST",
        };
      },
    }),
    getCustomerEmailConfig: builder.query({
      query: ({ id }) => ({
        url: `/customers/${id}/email-config`,
        method: "GET",
      }),
      providesTags: (_result, _error, { id }) => {
        return [{ type: "Customers", id }];
      },
    }),
    updateCustomerEmailConfig: builder.mutation({
      query({ id, data }) {
        return {
          url: `/customers/${id}/email-config`,
          method: "PUT",
          body: data,
        };
      },
      invalidatesTags: (_result, _error, { id }) => {
        return [{ type: "Customers", id }];
      },
    }),
    getCustomerApiKey: builder.query({
      query: ({ id }) => ({
        url: `/customers/${id}/api-key`,
        method: "GET",
      }),
      providesTags: (_result, _error, { id }) => {
        return [{ type: "Customers", id }];
      },
    }),
    createCustomerApiKey: builder.mutation({
      query({ id }) {
        return {
          url: `/customers/${id}/api-key`,
          method: "POST",
        };
      },
      invalidatesTags: (_result, _error, { id }) => {
        return [{ type: "Customers", id }];
      },
    }),
    revokeCustomerApiKey: builder.mutation({
      query({ id }) {
        return {
          url: `/customers/${id}/api-key`,
          method: "DELETE",
        };
      },
      invalidatesTags: (_result, _error, { id }) => {
        return [{ type: "Customers", id }];
      },
    }),
    getCustomer: builder.query({
      query: ({ id, ...rest }) => `customers/${id}?${getSearchParams(rest)}`,
      transformResponse: (response) => response,
      providesTags: (_result, _error, data) => {
        return [{ type: "Customers", id: data.id }];
      },
    }),
    getCustomerInvoices: builder.query({
      query: ({ id, ...rest }) =>
        `customers/${id}/invoices?${getSearchParams(rest)}`,
      // transformResponse: (response) => response,
      providesTags: (_result, _error, data) => {
        return [{ type: "Customers_Invoices", id: data.id }];
      },
    }),
    updateCustomer: builder.mutation({
      query({ id, data }) {
        return {
          url: `/customers/${id}`,
          method: "PUT",
          body: data,
        };
      },
      invalidatesTags: (_result, _error, { id }) => {
        return [{ type: "Customers", id }];
      },
    }),
    activateCustomer: builder.mutation({
      query({ parentCompany, data }) {
        return {
          url: `/customers/${parentCompany}/activate?${getSearchParams({
            parentCompany,
          })}`,
          method: "PUT",
          body: data,
          headers: {
            "x-parent-company": parentCompany,
            "customer-context": parentCompany,
          },
        };
      },
    }),
    updateCustomerLogo: builder.mutation({
      query({ id, data }) {
        return {
          url: `/customers/${id}/logo`,
          method: "PUT",
          body: data,
        };
      },
    }),
    deleteCustomer: builder.mutation({
      query({ id }) {
        return {
          url: `/customers/${id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Customers", id },
        { type: "Customers", id: "LIST" },
        { type: "AdminDashboard", id: "SUMMARY" },
      ],
    }),
    importCustomerCsv: builder.mutation({
      query(body) {
        return {
          url: "/customers/import/csv",
          method: "POST",
          body,
        };
      },
      invalidatesTags: [
        { type: "Clients", id: "LIST" },
        { type: "Locations", id: "LIST" },
        { type: "Jobs", id: "LIST" },
        { type: "Employees", id: "LIST" },
        { type: "Drivers", id: "LIST" },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetCustomersQuery,
  useAddCustomerMutation,
  useRequestCustomerInviteMutation,
  useResendCustomerInviteMutation,
  useGetCustomerEmailConfigQuery,
  useUpdateCustomerEmailConfigMutation,
  useGetCustomerApiKeyQuery,
  useCreateCustomerApiKeyMutation,
  useRevokeCustomerApiKeyMutation,
  useGetCustomerQuery,
  useGetCustomerInvoicesQuery,
  useUpdateCustomerMutation,
  useActivateCustomerMutation,
  useUpdateCustomerLogoMutation,
  useDeleteCustomerMutation,
  useImportCustomerCsvMutation,
} = customersApi;
